// src/native.js — Capacitor native abstractions med web fallback
// Dynamic imports bruger @vite-ignore så Vite ikke prøver at bundle native plugins

export const isNative = () =>
  typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

export const isIOS = () =>
  typeof window !== 'undefined' && window.Capacitor?.getPlatform?.() === 'ios';

export const isAndroid = () =>
  typeof window !== 'undefined' && window.Capacitor?.getPlatform?.() === 'android';

export async function hapticLight() {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import(/* @vite-ignore */ '@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {}
}

export async function hapticSuccess() {
  if (!isNative()) return;
  try {
    const { Haptics, NotificationType } = await import(/* @vite-ignore */ '@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Success });
  } catch {}
}

export async function takePhoto() {
  if (isNative()) {
    try {
      const { Camera, CameraResultType, CameraSource } = await import(/* @vite-ignore */ '@capacitor/camera');
      const photo = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
      });
      return { dataUrl: photo.dataUrl, blob: dataUrlToBlob(photo.dataUrl) };
    } catch (e) {
      if (e.message?.includes('cancelled') || e.message?.includes('User cancelled')) return null;
      throw e;
    }
  }
  return null;
}

export async function pickPhoto() {
  if (isNative()) {
    try {
      const { Camera, CameraResultType, CameraSource } = await import(/* @vite-ignore */ '@capacitor/camera');
      const photo = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
      });
      return { dataUrl: photo.dataUrl, blob: dataUrlToBlob(photo.dataUrl) };
    } catch (e) {
      if (e.message?.includes('cancelled') || e.message?.includes('User cancelled')) return null;
      throw e;
    }
  }
  return null;
}

function dataUrlToBlob(dataUrl) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}

// ── Barcode scanner ────────────────────────────────────────────────────────

// Kald ved app-start så ML Kit Vision framework er indlæst når brugeren scanner
export async function warmupBarcodeScanner() {
  if (!isNative()) return;
  try {
    const { BarcodeScanner } = await import(/* @vite-ignore */ '@capacitor-mlkit/barcode-scanning');
    await BarcodeScanner.isSupported();
    await BarcodeScanner.requestPermissions();
  } catch {}
}

export async function scanBarcode() {
  if (!isNative()) return null;

  try {
    const { BarcodeScanner, BarcodeFormat, LensFacing } =
      await import(/* @vite-ignore */ '@capacitor-mlkit/barcode-scanning');

    const { supported } = await BarcodeScanner.isSupported();
    if (!supported) return null;

    // Brug checkPermissions så vi undgår requestPermissions() når tilladelsen
    // allerede er givet — requestPermissions() kan på Android trigge en kort
    // pause/resume lifecycle-event der unmounter komponenten og swallower resultatet
    let camPermission;
    try {
      const checked = await BarcodeScanner.checkPermissions();
      camPermission = checked.camera;
    } catch {
      camPermission = 'prompt';
    }
    if (camPermission !== 'granted' && camPermission !== 'limited') {
      const requested = await BarcodeScanner.requestPermissions();
      if (requested.camera !== 'granted' && requested.camera !== 'limited') return null;
    }

    if (isAndroid()) {
      try {
        const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
        if (!available) {
          // Vent på at modulet er færdiginstalleret (state 3=CANCELED 4=COMPLETED 5=FAILED)
          await new Promise((resolve) => {
            const timeout = setTimeout(resolve, 30000);
            let listener = null;
            BarcodeScanner.addListener('googleBarcodeScannerModuleInstallProgress', (evt) => {
              if (evt.state >= 3 && evt.state <= 5) {
                clearTimeout(timeout);
                listener?.remove().catch(() => {});
                resolve();
              }
            }).then(l => { listener = l; }).catch(() => { clearTimeout(timeout); resolve(); });
            BarcodeScanner.installGoogleBarcodeScannerModule().catch(() => { clearTimeout(timeout); resolve(); });
          });
        }
      } catch {}
    }

    const { barcodes } = await BarcodeScanner.scan({
      formats: [
        BarcodeFormat.Ean13,
        BarcodeFormat.Ean8,
        BarcodeFormat.QrCode,
        BarcodeFormat.Code128,
        BarcodeFormat.Code39,
        BarcodeFormat.UpcA,
        BarcodeFormat.UpcE,
      ],
      lensFacing: LensFacing.Back,
    });

    return barcodes?.[0]?.rawValue?.trim() || null;
  } catch (e) {
    if (e.message?.includes('cancelled') ||
        e.message?.includes('User cancelled') ||
        e.message?.includes('cancel')) return null;
    console.error('Barcode scan fejl:', e);
    return null;
  }
}

export async function requestPushPermission() {
  if (!isNative()) return false;
  try {
    const { PushNotifications } = await import(/* @vite-ignore */ '@capacitor/push-notifications');
    const result = await PushNotifications.requestPermissions();
    return result.receive === 'granted';
  } catch { return false; }
}

// Sættes ved fejl så SettingsPage kan vise specifik fejlbesked
let _lastPushError = null;
export function getLastPushError() { return _lastPushError; }

export async function registerPushToken() {
  if (!isNative()) return null;
  _lastPushError = null;
  try {
    const { PushNotifications } = await import(/* @vite-ignore */ '@capacitor/push-notifications');
    return new Promise((resolve) => {
      let regHandle, errHandle;
      const timeout = setTimeout(() => {
        _lastPushError = 'Timeout: intet svar fra APNs efter 15s (tjek provisioning profile)';
        console.error('registerPushToken: timeout efter 15s');
        regHandle?.remove(); errHandle?.remove();
        resolve(null);
      }, 15000);
      const done = (val, errMsg) => {
        clearTimeout(timeout);
        if (errMsg) _lastPushError = errMsg;
        regHandle?.remove(); errHandle?.remove();
        resolve(val);
      };
      PushNotifications.addListener('registration', ({ value }) => {
        done(value);
      }).then(h => { regHandle = h; });
      PushNotifications.addListener('registrationError', (err) => {
        const msg = `registrationError: ${err?.error ?? JSON.stringify(err)}`;
        console.error('registerPushToken:', msg);
        done(null, msg);
      }).then(h => { errHandle = h; });
      PushNotifications.register().catch((err) => {
        const msg = `register() fejl: ${err?.message ?? String(err)}`;
        console.error('registerPushToken:', msg);
        done(null, msg);
      });
    });
  } catch (err) {
    _lastPushError = `uventet fejl: ${err?.message ?? String(err)}`;
    console.error('registerPushToken:', _lastPushError);
    return null;
  }
}

export async function addAppStateListener(onResume) {
  if (!isNative()) return;
  try {
    const { App } = await import(/* @vite-ignore */ '@capacitor/app');
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) onResume();
    });
  } catch {}
}

// ── OCR — scan tekst fra håndskrevet liste ────────────────────────────────
// iOS:     @capacitor-community/image-to-text  (CoreML Vision, ingen ML Kit)
// Android: @pantrist/capacitor-plugin-ml-kit-text-recognition (Google Play ML Kit)
export async function scanTextOCR() {
  if (!isNative()) return null;
  try {
    const { Camera, CameraResultType, CameraSource } =
      await import(/* @vite-ignore */ '@capacitor/camera');

    if (isIOS()) {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        quality: 85,
        allowEditing: false,
      });
      const iosPath = photo.path ?? photo.webPath;
      console.log('[OCR iOS] photo.path:', photo.path, 'photo.webPath:', photo.webPath);
      if (!iosPath) throw new Error('Ingen billedsti modtaget fra kamera');
      const { Ocr } = await import('@capacitor-community/image-to-text');
      const data = await Ocr.detectText({ filename: iosPath });
      console.log('[OCR iOS] rå resultat:', JSON.stringify(data).slice(0, 300));
      if (!data.textDetections?.length) return null;
      return data.textDetections.map(d => d.text).join('\n');
    } else {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
        quality: 85,
        allowEditing: false,
      });
      if (!photo.base64String) throw new Error('Ingen billeddata modtaget fra kamera');
      const { CapacitorPluginMlKitTextRecognition } = await import('@pantrist/capacitor-plugin-ml-kit-text-recognition');
      const data = await CapacitorPluginMlKitTextRecognition.detectText({ base64Image: photo.base64String });
      console.log('[OCR Android] rå resultat:', JSON.stringify(data).slice(0, 300));
      if (!data.text?.trim()) return null;
      return data.text;
    }
  } catch (err) {
    if (err?.message?.includes('cancelled') || err?.message?.includes('cancel')) return null;
    console.error('[OCR] fejl:', err);
    throw err;
  }
}
